import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QuestionHeader from "../QuestionHeader";
import { LeaseApplicationService, LeaseApplicationTemplateSectionVM, QuestionTypeEnum, TemplateEntityTypesEnum, TemplateService, TemplateTypeEnum } from "@propertelligent/client-api";
import { useNavigate, useParams, useLocation } from "react-router";
import "./AddTemplate.css"
import RequestBreadcrumb from "../../../common/RequestBreadcrumb";
import AddTraspButton from "../../../firstTimeUserExperience/commonCom/AddTraspButton";
import AddButtonWithArrow from "../../../firstTimeUserExperience/commonCom/AddButtonWithArrow";
import QuestionOptional from "../QuestionOptional";
import EmploymentHistoryEdit from "../EmploymentHistoryEdit";
import Reference from "../RefrenceEdit";
import QuestionHistoryEdit from "../QuestionEditTemplete";
import FeeTemplete from "../FeeTemplete";
import ConfirmSignature from "../ConfirmSignature";
import BoardingReview from "../../../TenantOnboard/RenderSteps/BoardingReview";
import SimpleSpinner from "../../../loader";
import apiHelper from "../../../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../../../utils/pubsub";
import { UserLoginData } from "../../../common/charts/Enums";
import { GetImages } from "../../../../utils/GetImages";
import SectionHeader from "../../../TenantOnboard/Header/SectionHeader";
import CustomTooltip from "../../../common/CustomTooltip";
import { OverlayTrigger } from "react-bootstrap";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PermissionGuard } from "../../../../newComponents/PermissionGuard";
import NoAccess from "../../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { formatLabel, isDeepEqual } from "../../../../utils/helper";
import { validateQuestionnaireQuestions, withFollowUpChoiceDefaults } from "../questionnaireValidation";
import { exceedsTemplateTextLimit, templateTextLimitMessage } from "../templateTextLimits";

const SIGNATURE_LABEL = "Signature text";
const SIGNATURE_REQUIRED_MESSAGE = "Signature text is required";
const FEE_CLAUSE_LABEL = "Application Fee Clause";

const AddTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [activeSection, setActiveSection] = useState("");
  const [previewApplication, setPreviewApplication] = useState(false)
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [sectionSnapshot, setSectionSnapshot] = useState<{ key: string; value: any } | null>(null);
  const [signatureError, setSignatureError] = useState(false);
  const [signatureErrorMessage, setSignatureErrorMessage] = useState(SIGNATURE_REQUIRED_MESSAGE);
  const [feeErrors, setFeeErrors] = useState<{ fee?: boolean; clause?: boolean; clauseMessage?: string }>({});
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [templateLoading, setTemplateLoading] = useState(!!id);
  const [initialData, setInitialData] = useState<any | null>(null);
  const [postData, setPostData] = useState<any | undefined>(
    {
      name: "New Tenant Application",
      description: "Application for prospective tenants",
      templateTypeId: TemplateTypeEnum.LeaseApplication,
      entityTypeId: TemplateEntityTypesEnum.Organization,
      entityId: parseInt(sessionStorage.getItem(UserLoginData.organization_id)),
      isInUse: false,
      leaseApplicationTemplateSections: {
        CreditCheck: { requiredRange: 2 },
        CurrentAddress: { requiredRange: 2 },
        RentIncomeCheck: { requiredRange: 2 },
        EmploymentHistory: { requiredRange: 2 },
        References: { requiredRange: 2 },
        Questionnaires: {
          questions: [{
            id: 1, questionTypeId: QuestionTypeEnum.SingleChoice,
            questionText: "Do you smoke?", isRequired: false,
            choices: [{ id: 1, optionText: "Yes" }, { id: 2, optionText: "No" }],
            followUpQuestionChoiceId: 1, followUpQuestionText: "Please provide details?"
          }]
        },
        Signature: { clause: "Are you agree for everything" },
        ApplicationFees: { clause: " Fee is non refundable", fee: 50 }
      } as LeaseApplicationTemplateSectionVM,
    })

  const postDataRef = useRef(postData);
  useEffect(() => { postDataRef.current = postData; }, [postData]);

  const check = usePermissions();
  const canWrite = check.canWrite("Templates");

  const getSampleData = () => {
    apiHelper(LeaseApplicationService.getApiLeaseApplicationSampleData, {
      showNotification: false,
    }).then((res) => { }).catch((err) => { })
  }

  useEffect(() => {
    if (id) { getTempleteData() }
    getSampleData()
  }, [id]);

  const handleSubmit = useCallback(async () => {
    const questionnaireCheck = validateQuestionnaireQuestions(
      postData?.leaseApplicationTemplateSections?.Questionnaires?.questions,
    );
    if (!questionnaireCheck.ok) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        title: "Failure",
        message: questionnaireCheck.message,
      });
      return;
    }
    const signatureClause = postData?.leaseApplicationTemplateSections?.Signature?.clause;
    if (!signatureClause || !signatureClause.trim()) {
      setSignatureErrorMessage(SIGNATURE_REQUIRED_MESSAGE);
      setSignatureError(true);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        title: "Failure",
        message: "Signature text is required before saving the template.",
      });
      return;
    }
    const overLimitClause = [
      { value: signatureClause, label: SIGNATURE_LABEL },
      { value: postData?.leaseApplicationTemplateSections?.ApplicationFees?.clause, label: FEE_CLAUSE_LABEL },
    ].find((c) => exceedsTemplateTextLimit(c.value));
    if (overLimitClause) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        title: "Failure",
        message: templateTextLimitMessage(overLimitClause.label),
      });
      return;
    }
    try {
      setIsLoading(true);
      const payload = withFollowUpChoiceDefaults(postData);
      const apiCall = id
        ? apiHelper(TemplateService.putApiTemplate, {
          title:"Template Updated",
          successMessage: "Template edited successfully",
          failureMessage: "Unable to Add Template",
          showNotification: true,
        }, Number(id), payload)
        : apiHelper(TemplateService.postApiTemplate, {
          title:"Template Saved",
          successMessage: "Template saved successfully",
          failureMessage: "Unable to create Template",
          showNotification: true,
        }, payload)
      const res = await apiCall;
      if (res?.data?.success) {
        navigate("/organization/templates", { state: { activeTab: location.state?.fromTemplateTab || "LeaseApplication" } });
        //  setActiveSection("")
      }
    } catch (error) {
      console.error("Error occurred while submitting data:", error);
    } finally { setIsLoading(false); }
  }, [id, postData, navigate]);

  const getTempleteData = () => {
    setTemplateLoading(true);
    apiHelper(TemplateService.getApiTemplate, { showNotification: false, }, Number(id))
      .then((res) => {
        if (res?.data?.success) {
          setPostData(res?.data?.success);
          // Separate copy so in-place edits to postData can never leak into the dirty baseline.
          setInitialData(structuredClone(res?.data?.success));
          // res undefined means apiHelper already handled a 401 (logout flow); any 5xx status
          // means it already surfaced the ServerError modal -- don't double up with our own toast.
        } else if (res && !(res.statusCode >= 500 && res.statusCode < 600)) {
          PubSub.publish(TopicsEnum.Notification.toString(), {
            type: NotificationType.Failure,
            title: "Failure",
            message: "Unable to load template details",
          });
        }
      })
      .catch((err) => { })
      .finally(() => setTemplateLoading(false));
  };

  const isDirty = useMemo(() => {
    if (!id) return true;
    if (!initialData || templateLoading) return false;
    return !isDeepEqual(postData, initialData);
  }, [id, initialData, postData, templateLoading]);

  const duplicateTemplete = () => {
    if (id) {
      apiHelper(TemplateService.postApiTemplateDuplicate, {
        title:"Template duplicated",
        successMessage: "Template duplicated successfully",
        failureMessage: "Unable to Duplicate Template",
        showNotification: true,
      }, Number(id)).then((res) => {
        if (res?.data?.success) { navigate(`/organization/templates/edit/${res?.data?.success.id}`); }
      }).catch((err) => { });
    }
  };

  const deleteTemplete = () => {
    if (id) {
      apiHelper(TemplateService.deleteApiTemplate, {
        title:"Template Deleted",
        successMessage: "Template deleted successfully",
        failureMessage: "Unable to Delete Template",
        showNotification: true,
      }, Number(id)).then((res) => {
        navigate("/organization/templates", { state: { activeTab: location.state?.fromTemplateTab || "LeaseApplication" } });
      }).catch((err) => { });
    }
  };

  const HandleEditFeature = (sectionName: string) => {
    if (sectionName) {
      setSectionSnapshot({
        key: sectionName,
        value: postDataRef.current?.leaseApplicationTemplateSections?.[sectionName],
      });
      setSignatureError(false);
      setFeeErrors({});
      setActiveSection(sectionName);
    } else { setActiveSection("") }
  };

  const handleSectionToggle = useCallback((sectionName: string) => {
    setPostData((prevData) => {
      const newData = { ...prevData };
      newData.leaseApplicationTemplateSections = { ...(newData.leaseApplicationTemplateSections || {}) };
      if (newData.leaseApplicationTemplateSections[sectionName]) {
        delete newData.leaseApplicationTemplateSections[sectionName];
      } else {
        newData.leaseApplicationTemplateSections[sectionName] = getDefaultSectionDetails(sectionName);
      }

      return newData;
    });
  }, [setPostData]);

  const getDefaultSectionDetails = (sectionName: string) => {
    const updatedQuestions = [
      ...(postData?.leaseApplicationTemplateSections?.Questionnaires?.questions || []),
    ];
    switch (sectionName) {
      case "RentIncomeCheck":
      case "CreditCheck":
      case "CurrentAddress":
      case "EmploymentHistory":
      case "References":
        return { requiredRange: 2 };
      case "Questionnaires":
        return {
          questions: [
            {
              id: updatedQuestions?.length + 1,
              questionTypeId: 1,
              questionText: "Do you smoke?",
              choices: [
                { id: 1, optionText: "Yes" },
                { id: 2, optionText: "No" },
              ],
              followUpQuestionChoiceId: 1,
              followUpQuestionText: "Please provide details?",
            },
          ],
          requiredRange: 8,
        };
      case "ApplicationFees":
        return { clause: "Fee is non refundable", fee: 50 };
      case "Signature":
        return { clause: "Are You agree for everything" };
      default:
        return {};
    }
  };

  const getSectionData = () => {
    setSectionsLoading(true);
    apiHelper(LeaseApplicationService.getApiLeaseApplicationLeaseApplicationSectionTypes, {
      showNotification: false,
    }).then((res) => {
      if (!res?.data?.success) {
        // res undefined means apiHelper already handled a 401 (logout flow); any 5xx status
        // means it already surfaced the ServerError modal -- don't double up with our own toast.
        if (res && !(res.statusCode >= 500 && res.statusCode < 600)) {
          PubSub.publish(TopicsEnum.Notification.toString(), {
            type: NotificationType.Failure,
            title: "Failure",
            message: "Unable to load application sections",
          });
        }
        return;
      }
      const updatedData = res?.data?.success?.map((section) => ({
        ...section,
        editable: ![1, 2, 3].includes(section.id),
        onClick: canWrite ? () => handleSectionToggle(section.name.replace(/\s/g, "")) : undefined,
        editClick: canWrite ? () => HandleEditFeature(section.name.replace(/\s/g, "")) : undefined
      }));
      setData(updatedData);
    })
      .catch((err) => { })
      .finally(() => setSectionsLoading(false));
  }
  useEffect(() => {
    getSectionData()
  }, [id, canWrite]);

  const isContentLoading = sectionsLoading || templateLoading;

  return (
    <PermissionGuard permission="Templates" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className="application-section">
        {isContentLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "100%" }}>
            <SimpleSpinner myClassName="Icon50" />
          </div>
        ) : (
        <>
        <QuestionHeader
          postData={postData}
          setPostData={setPostData}
          duplicateTemplete={duplicateTemplete}
          deleteTemplete={deleteTemplete}
          previewApplication={previewApplication}
        />
        <div className="application-section-main">
          <section className="application_review">
            <div className="application_review_left">
              <QuestionOptional
                data={data}
                postData={postData}
                activeSection={activeSection}
              />

            </div>
            
            <div className="application_review_right ps-3">
              {activeSection && (
              <div className="onboardBoxContainer" >
                 <SectionHeader title={formatLabel(activeSection)} icon={false}  subTitle={activeSection === "EmploymentHistory" ? 
                  "Please Specify Years of Employment History that will be Required From the Tenant" :
                  activeSection === "References" ? "Please Specify No of Reference(s)  that will be Required From the Tenant"  :""} />
                <div className="sectionStep w-100 ">
                {activeSection === "EmploymentHistory" && (<EmploymentHistoryEdit postData={postData} setPostData={setPostData} />)}
              {activeSection === "References" && (<Reference postData={postData} setPostData={setPostData} />)}
              {activeSection === "Questionnaires" && (<QuestionHistoryEdit postData={postData} setPostData={setPostData} />)}
              {activeSection === "ApplicationFees" && (<FeeTemplete postData={postData} setPostData={setPostData} feeError={feeErrors.fee} clauseError={feeErrors.clause} clauseErrorMessage={feeErrors.clauseMessage} />)}
              {activeSection === "Signature" && (
                <ConfirmSignature
                  postData={postData}
                  setPostData={(updated) => { setSignatureError(false); setPostData(updated); }}
                  isError={signatureError}
                  errorMessage={signatureErrorMessage}
                />
              )}
              </div>
             <div className="application-section-bttom-view1 ">
            <AddTraspButton
              type="button"
              bname="Cancel"
              onClick={() => {
                if (sectionSnapshot) {
                  setPostData((prev) => {
                    const newSections = { ...(prev?.leaseApplicationTemplateSections || {}) };
                    if (sectionSnapshot.value === undefined) {
                      delete newSections[sectionSnapshot.key];
                    } else {
                      newSections[sectionSnapshot.key] = sectionSnapshot.value;
                    }
                    return { ...prev, leaseApplicationTemplateSections: newSections };
                  });
                }
                setSectionSnapshot(null);
                setSignatureError(false);
                setFeeErrors({});
                setActiveSection("");
              }}
              border={false}
              width="fit-content"
            />

          {canWrite && (
          <AddButtonWithArrow
            type="button"
            className={previewApplication ? "bg-secondary" : ""}
            buttonname="Save"
            onClick={() => {
              if (activeSection === "Signature") {
                const clause = postData?.leaseApplicationTemplateSections?.Signature?.clause;
                if (!clause || !clause.trim()) {
                  setSignatureErrorMessage(SIGNATURE_REQUIRED_MESSAGE);
                  setSignatureError(true);
                  return;
                }
                if (exceedsTemplateTextLimit(clause)) {
                  setSignatureErrorMessage(templateTextLimitMessage(SIGNATURE_LABEL));
                  setSignatureError(true);
                  return;
                }
              }
              if (activeSection === "ApplicationFees") {
                const fee = postData?.leaseApplicationTemplateSections?.ApplicationFees?.fee;
                const clause = postData?.leaseApplicationTemplateSections?.ApplicationFees?.clause;
                const feeMissing = fee === undefined || fee === null || String(fee).trim() === "";
                const clauseMissing = !clause || !String(clause).trim();
                const clauseTooLong = exceedsTemplateTextLimit(clause);
                if (feeMissing || clauseMissing || clauseTooLong) {
                  setFeeErrors({
                    fee: feeMissing,
                    clause: clauseMissing || clauseTooLong,
                    clauseMessage: clauseTooLong ? templateTextLimitMessage(FEE_CLAUSE_LABEL) : undefined,
                  });
                  return;
                }
              }
              if (activeSection === "Questionnaires") {
                const questionnaireCheck = validateQuestionnaireQuestions(
                  postData?.leaseApplicationTemplateSections?.Questionnaires?.questions,
                );
                if (!questionnaireCheck.ok) {
                  PubSub.publish(TopicsEnum.Notification.toString(), {
                    type: NotificationType.Failure,
                    title: "Failure",
                    message: questionnaireCheck.message,
                  });
                  return;
                }
              }
              const sectionLabel = data?.find((d: any) => d.name.replace(/\s+/g, "") === activeSection)?.name || activeSection;
              PubSub.publish(TopicsEnum.Notification.toString(), {
                type: NotificationType.Success,
                title: "Success",
                message: `${sectionLabel} section updated`,
              });
              setSectionSnapshot(null);
              setSignatureError(false);
              setFeeErrors({});
              setActiveSection("");
            }}
            minWidth="100px"
          />
          )}
        </div>
              </div>
              )}
              {!activeSection && (
                <>                                        
                  <div className="application-preview-header">                     
                    <p className="heading black">Lease Application Preview <span> <CustomTooltip children={<img className="color-white-icon" alt="application info" src={GetImages.InfoIcon} />} content={"Not actual data: This Preview is for Visualization Purposes Only and Does Not Contain Real Data."} /></span></p>                  
                  </div>
                  <BoardingReview
                    postData={postData}
                    editable={false}
                    isEditClick={undefined}
                    formData={undefined}
                    setFormData={undefined}
                    steps={undefined}
                    isPreview={true}
                  />
                </>
              )}
            </div>
          </section>
        </div>
        {!activeSection && (
        <div className="application-section-bttom-view1 ">
          {!activeSection ?
            <AddTraspButton
              type="button"
              bname="Cancel"
              onClick={() => { navigate(-1) }}
              border={false}
              width="fit-content"
            /> :
            <AddTraspButton
              type="button"
              bname="Cancel"
              onClick={() => { setActiveSection("") }}
              border={false}
              width="fit-content"
            />
          }

          {canWrite && (
          <AddButtonWithArrow
            type="button"
            className={previewApplication ? "bg-secondary" : ""}
            buttonname="Save Template"
            onClick={handleSubmit}
            disabled={isLoading || !isDirty}
            loading={isLoading}
            minWidth="100px"
          />
          )}
        </div>
        )}
        </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default AddTemplate;
